import { useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useProjectMembers } from '@/hooks/use-projects';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import {
  selectAllState,
  splitProjectPeople,
  toggleAll,
  togglePerson,
} from '../lib/project-member-choice';

const NOBODY: ReadonlySet<string> = new Set();

/**
 * The project's people as checkboxes, with "Select all" on top (HW-983):
 * linking a board to a project gives nobody access, so whoever should work on
 * the board is ticked here and becomes a board Member. People already on the
 * board are listed ticked and fixed — adding them changes nothing. People who
 * no longer work here are not listed; the server refuses them too.
 */
export function ProjectMembersPicker({
  projectId,
  onBoard = NOBODY,
  onBoardNote = 'already on the board',
  labelledBy,
  value,
  onChange,
}: {
  projectId: string;
  /** Employee ids the board already has as members. */
  onBoard?: ReadonlySet<string>;
  /** Said beside each of `onBoard`. */
  onBoardNote?: string;
  /** Id of the text that names the list, for assistive technology. */
  labelledBy?: string;
  value: string[];
  onChange: (employeeIds: string[]) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const { data: members, isLoading, isError } = useProjectMembers(projectId);
  const { offered, alreadyOnBoard } = useMemo(
    () => splitProjectPeople(members, onBoard),
    [members, onBoard]
  );
  const offeredIds = useMemo(() => offered.map((m) => m.employeeId), [offered]);

  if (isLoading) return <Skeleton className="h-16 rounded-md" />;
  if (isError) {
    return <p className="text-xs text-destructive">The project's members could not be loaded.</p>;
  }
  if (offered.length === 0 && alreadyOnBoard.length === 0) {
    return <p className="text-xs text-muted-foreground">The project has no members to add.</p>;
  }

  const all = selectAllState(offeredIds, value);
  return (
    <div role="group" aria-labelledby={labelledBy} className="rounded-md border border-border">
      {offered.length > 0 && (
        <label className="flex items-center gap-2 border-b border-border px-3 py-2 text-xs font-medium cursor-pointer">
          <Checkbox checked={all} onCheckedChange={() => onChange(toggleAll(offeredIds, value))} />
          Select all ({offered.length})
        </label>
      )}
      <ul className="max-h-48 overflow-y-auto py-1">
        {offered.map((m) => (
          <li key={m.employeeId}>
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-muted/50">
              <Checkbox
                checked={value.includes(m.employeeId)}
                onCheckedChange={() => onChange(togglePerson(value, m.employeeId))}
              />
              <span className="truncate">{formatName(m)}</span>
              {m.isManager && <span className="text-[10px] text-muted-foreground">manager</span>}
            </label>
          </li>
        ))}
        {alreadyOnBoard.map((m) => (
          <li key={m.employeeId}>
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground">
              <Checkbox checked disabled />
              <span className="truncate">{formatName(m)}</span>
              <span className="text-[10px]">{onBoardNote}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
